import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Command, Sparkles, TrendingUp, MapPin, Layers, Users, ShieldAlert } from "lucide-react";

const KPI = ({ label, value, hint, testid }) => (
  <div className="cad-panel p-4" data-testid={testid}>
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{label}</div>
    <div className="cad-kpi-value text-3xl mt-1 text-[#F6F8FA]">{value}</div>
    {hint && <div className="text-[11px] text-[#B3C2CC] mt-1 font-mono-cad">{hint}</div>}
  </div>
);

export const MiningMigrationCommand = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["mmc-progress"],
    queryFn: async () => (await api.get("/cadastre360/migration/progress")).data,
    refetchInterval: 60000,
  });
  const dq = useQuery({
    queryKey: ["mmc-dq"],
    queryFn: async () => (await api.get("/cadastre360/migration/data-quality")).data,
    refetchInterval: 90000,
  });
  const goLive = useQuery({
    queryKey: ["mmc-golive"],
    queryFn: async () => (await api.get("/cadastre360/migration/go-live")).data,
  });

  const p = data;

  const headlines = p ? [
    `Mining migration completion at ${p.completion_pct}% — ${p.records_committed.toLocaleString()} claims committed to register, ${p.submissions_received.toLocaleString()} submissions received.`,
    p.by_province?.[0] && `Top-performing province this cycle: ${p.by_province[0].province} (${p.by_province[0].rate}%).`,
    p.by_commodity?.[0] && `Leading commodity by submission volume: ${p.by_commodity[0].commodity} (${p.by_commodity[0].total.toLocaleString()} records).`,
    `Projected completion of legacy mining claim migration: ${p.projected_eta} at current throughput of ${p.weekly_throughput.toLocaleString()}/week.`,
    `${p.awaiting_officer.toLocaleString()} claims currently awaiting officer review; ${p.awaiting_claimant.toLocaleString()} awaiting claimant correction; ${p.awaiting_survey.toLocaleString()} awaiting surveyor validation.`,
  ].filter(Boolean) : [];

  return (
    <div className="space-y-6" data-testid="page-mining-migration-command">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">
          Cadastre360™ — Mining Cadastre Migration Acceleration Layer
        </div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <Command className="text-[#C99A2E]" /> Mining Migration Progress Command Centre
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1 max-w-4xl leading-relaxed">
          Executive view of Zimbabwe's mining cadastre legacy migration. Auto-refreshes every 60 s. This is a new command centre;
          the existing National Situation Room remains unchanged.
        </div>
      </header>

      <div className="cad-panel p-5" data-testid="mmc-headlines">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3">Executive headlines</div>
        <ol className="space-y-2">
          {headlines.map((h, i) => (
            <li key={i} data-testid={`mmc-headline-${i}`} className="flex items-start gap-2 text-[14px] text-[#F6F8FA] leading-relaxed">
              <Sparkles size={14} className="text-[#C99A2E] mt-1" />{h}
            </li>
          ))}
          {isLoading && <li className="text-[#B3C2CC] italic text-[13px]">Loading intelligence…</li>}
        </ol>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KPI label="Expected" value={(p?.total_expected ?? 0).toLocaleString()} testid="mmc-kpi-expected" />
        <KPI label="Submitted" value={(p?.submissions_received ?? 0).toLocaleString()} testid="mmc-kpi-submitted" />
        <KPI label="Validated" value={(p?.records_validated ?? 0).toLocaleString()} testid="mmc-kpi-validated" />
        <KPI label="Approved" value={(p?.records_approved ?? 0).toLocaleString()} testid="mmc-kpi-approved" />
        <KPI label="Committed" value={(p?.records_committed ?? 0).toLocaleString()} hint={`${p?.completion_pct ?? 0}%`} testid="mmc-kpi-committed" />
        <KPI label="Projected ETA" value={p?.projected_eta ?? "—"} hint={`${p?.weekly_throughput ?? 0}/wk`} testid="mmc-kpi-eta" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3 flex items-center gap-1.5">
            <TrendingUp size={12} /> Provincial performance
          </div>
          <table className="w-full text-[12.5px]">
            <thead><tr className="text-left border-b cad-hairline">
              {["Province", "Total", "Committed", "Rate"].map((h) => (
                <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
              ))}
            </tr></thead>
            <tbody className="cad-tbody">
              {(p?.by_province || []).map((r) => (
                <tr key={r.province}>
                  <td className="px-2 py-1.5 text-[#F6F8FA] flex items-center gap-1.5"><MapPin size={11} className="text-[#C99A2E]" />{r.province}</td>
                  <td className="px-2 py-1.5 font-mono-cad text-[#B3C2CC]">{r.total.toLocaleString()}</td>
                  <td className="px-2 py-1.5 font-mono-cad text-[#B3C2CC]">{r.committed.toLocaleString()}</td>
                  <td className="px-2 py-1.5 font-mono-cad text-[#6BB88A]">{r.rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3 flex items-center gap-1.5">
            <Layers size={12} /> By commodity
          </div>
          <div className="space-y-2 mt-2">
            {(p?.by_commodity || []).map((c) => (
              <div key={c.commodity}>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-[#F6F8FA]">{c.commodity}</span>
                  <span className="font-mono-cad text-[#C99A2E]">{c.total.toLocaleString()}</span>
                </div>
                <div className="h-1.5 bg-[#18232D] rounded-full overflow-hidden mt-1">
                  <div className="h-full bg-[#C99A2E]" style={{ width: `${Math.min(100, (c.total / ((p?.by_commodity?.[0]?.total) || 1)) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3 flex items-center gap-1.5">
            <Users size={12} /> Officer productivity
          </div>
          <table className="w-full text-[12.5px]">
            <thead><tr className="text-left border-b cad-hairline">
              {["Officer", "Reviews"].map((h) => (
                <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
              ))}
            </tr></thead>
            <tbody className="cad-tbody">
              {(p?.officer_productivity || []).map((o) => (
                <tr key={o.officer}>
                  <td className="px-2 py-1.5 text-[#F6F8FA] font-mono-cad text-[11px]">{o.officer}</td>
                  <td className="px-2 py-1.5 font-mono-cad text-[#6BB88A]">{o.reviews}</td>
                </tr>
              ))}
              {(!p?.officer_productivity || p.officer_productivity.length === 0) &&
                <tr><td className="px-2 py-2 text-[#7C93A5] italic text-[12px]" colSpan={2}>No officer reviews yet</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3 flex items-center gap-1.5">
            <ShieldAlert size={12} /> Provincial go-live posture
          </div>
          <table className="w-full text-[12.5px]">
            <thead><tr className="text-left border-b cad-hairline">
              {["Province", "Status", "Readiness"].map((h) => (
                <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
              ))}
            </tr></thead>
            <tbody className="cad-tbody">
              {(goLive.data || []).map((g) => (
                <tr key={g.id}>
                  <td className="px-2 py-1.5 text-[#F6F8FA]">{g.province}</td>
                  <td className="px-2 py-1.5"><span className="cad-chip cad-chip-gold text-[9px] uppercase">{g.status.replace(/_/g, " ")}</span></td>
                  <td className="px-2 py-1.5 font-mono-cad">{g.readiness_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="cad-panel p-5">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3">Surveyor submission activity</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {(p?.surveyor_activity || []).map((s) => (
            <div key={s.surveyor} className="border cad-hairline rounded-sm px-3 py-2 bg-[#071B2B]">
              <div className="text-[13px] text-[#F6F8FA] font-display font-bold truncate">{s.surveyor}</div>
              <div className="text-[10px] font-mono-cad text-[#C99A2E] mt-1">{s.submissions} submission{s.submissions === 1 ? "" : "s"}</div>
            </div>
          ))}
          {(!p?.surveyor_activity || p.surveyor_activity.length === 0) &&
            <div className="text-[#7C93A5] italic text-[12px]">No surveyor submissions yet</div>}
        </div>
      </div>

      <div className="text-[10px] font-mono-cad uppercase tracking-widest text-[#5F7688]">
        Demonstration data — refresh {p?.generated_at?.slice(0, 19)}
      </div>
    </div>
  );
};
